import { StateCreator } from 'zustand'

export interface GlobalTask {
  id: string
  title: string
  description?: string
  type: 'pdf-generation' | 'document-analysis' | 'file-upload' | 'general'
  progress?: number // 0-100 percentage
  startTime: Date
  metadata?: Record<string, any>
}

export interface GlobalTaskSlice {
  globalTasks: {
    tasks: Record<string, GlobalTask>
    activeTasks: string[] // Array of task IDs in order they were added
  }

  addGlobalTask: (task: Omit<GlobalTask, 'startTime'>) => void
  updateGlobalTask: (
    id: string,
    updates: Partial<Omit<GlobalTask, 'id' | 'startTime'>>
  ) => void
  removeGlobalTask: (id: string) => void
  clearAllTasks: () => void
  getActiveTask: () => GlobalTask | null // Returns the most recent active task
}

export const createGlobalTaskSlice: StateCreator<GlobalTaskSlice> = (
  set,
  get
) => ({
  globalTasks: {
    tasks: {},
    activeTasks: [],
  },

  addGlobalTask: (task) => {
    const newTask: GlobalTask = {
      ...task,
      startTime: new Date(),
    }

    set((state) => ({
      globalTasks: {
        tasks: {
          ...state.globalTasks.tasks,
          [task.id]: newTask,
        },
        activeTasks: [...state.globalTasks.activeTasks, task.id],
      },
    }))
  },

  updateGlobalTask: (id, updates) => {
    set((state) => {
      if (!state.globalTasks.tasks[id]) {
        return state // Task doesn't exist, no update
      }

      return {
        globalTasks: {
          ...state.globalTasks,
          tasks: {
            ...state.globalTasks.tasks,
            [id]: {
              ...state.globalTasks.tasks[id],
              ...updates,
            },
          },
        },
      }
    })
  },

  removeGlobalTask: (id) => {
    set((state) => {
      const { [id]: removedTask, ...remainingTasks } = state.globalTasks.tasks
      return {
        globalTasks: {
          tasks: remainingTasks,
          activeTasks: state.globalTasks.activeTasks.filter(
            (taskId) => taskId !== id
          ),
        },
      }
    })
  },

  clearAllTasks: () => {
    set({
      globalTasks: {
        tasks: {},
        activeTasks: [],
      },
    })
  },

  getActiveTask: () => {
    const state = get()
    const activeTasks = state.globalTasks.activeTasks
    if (activeTasks.length === 0) return null

    // Return the most recent task (last in the array)
    const latestTaskId = activeTasks[activeTasks.length - 1]
    return state.globalTasks.tasks[latestTaskId] || null
  },
})
